import * as _ from 'lodash';
import { ChangeDetectorRef, Component, HostListener, Input, OnInit, OnDestroy, AfterContentInit, ChangeDetectionStrategy, AfterContentChecked, AfterViewInit, EventEmitter, Output } from '@angular/core';
import { NgbActiveModal, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { filter, map, switchMap, takeUntil } from "rxjs/operators";
import { Observable, of } from 'rxjs';

import { slidersConfig, slidersGames } from './slider.config';
import { GamesSlidersConfig, GameTypesEnum, SingleGameConfig } from './slider.models';
import { GamesStore } from "../core-services/games/games.store";
import { BaseComponent } from "../base.component";
import { GamesDetailsComponent } from '../game-details/games-details.component';
import { Game, GameTypeGames } from '../core-services/games/games.model';
import { FavoritesStore } from '../core-services/favorites/favorites.store';
import { GameJackpotStore } from '../core-services/game-jackpots/game-jackpots.store';
import { VisitorStore } from '../core-services/visitor/visitor.store';
import { GameFilterCategoriesService } from '../games-filter/games-filter-categories.service';
import { JackpotsCurrencyWithGames } from '../core-services/game-jackpots/game-jackpots.model';
import { PrefixLangs } from '../core-services/core.models';

export const WHG_SLIDER_SELECTOR = 'whg-slider';

/*
*     Angular does not support embedding one web component into another,
*     Therefore we need to define this component with seperete selector to use it both externally
*     and within other Angular ELements,
*     see: https://github.com/angular/angular/issues/25525
*/
@Component({
  selector: `${WHG_SLIDER_SELECTOR}-internal`,
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.scss'],
  providers: [GameFilterCategoriesService],
})
export class SliderComponent extends BaseComponent implements OnInit, OnDestroy, AfterContentChecked, AfterViewInit {

  @HostListener('window:popstate', ['$event'])
  dismissModal() {
    this.modalService.dismissAll();
  }

  readonly typeConfig: GamesSlidersConfig = slidersConfig;
  readonly gameTypes = GameTypesEnum;

  type: SingleGameConfig;
  launchCodes: string[] = [];
  imgCodes: [{
    launchCode?: string;
    imgCode?: string;
  }] = [{}];
  amounts: string[] = [];
  currencySymbol$: Observable<string> = this.visitorStore.currencySymbol$;
  currencyCode$: Observable<string> = this.visitorStore.currencyCode$;

  loader: boolean = true;
  gameName: string;

  @Input() title: string;
  @Input() visualType: GameTypesEnum;
  @Input() currentGame: Game;
  @Input() isInModal: boolean = false;
  @Input() isInSearch: boolean = false;
  @Output() gameOpenedFromSlider?= new EventEmitter<boolean>();

  get isGameCategoryOpen(): boolean {
    return this.coreService.hideContentIfGameCategoryOpened();
  }

  get langPrefix(): string {
    return this.coreService.getLangPrefix(this.jurisdiction, PrefixLangs.Slider);
  }

  constructor(
    private modalService: NgbModal,
    private gameStore: GamesStore,
    private cdr: ChangeDetectorRef,
    private favoritesStore: FavoritesStore,
    private jackpotsStore: GameJackpotStore,
    private visitorStore: VisitorStore,
    private gameFilterCategoriesService: GameFilterCategoriesService,
    public activeModal: NgbActiveModal
  ) {
    super();
  }

  ngAfterContentChecked() {
    this.cdr.detectChanges();
  }

  ngOnInit() {
    // Used for skeleton loader as not all slider categories will have launch codes
    setTimeout(() => {
      this.loader = false;
    }, 3000);

    this.gameStore.getGames(); // load games and prepare for the game details modal
    this.type = this.typeConfig[this.visualType];
  }

  ngAfterViewInit() {


    switch (this.visualType) {
      /*
    * @TODO - featured games uses a different width/height for the game image,
    * we need to have that image available for the specific games that Skol wants to promote
    * */
      case (GameTypesEnum.Featured):
        this.launchCodes = ['RLX3SECRETCITIES', 'BPFISHINFRENZY', 'PNGBOOKOFDEAD', 'MGHYPERSTRIKE', 'BPEYEOFHORUS'];
        break;

      /*
     * @TODO - new games do not follow algorithm, a list of launchcodes needs to be provided
     * We might be able to use a property of the game
     * */
      case (GameTypesEnum.NewGames):
        this.gameStore.getNewToSkolGames();
        const newToSkolGames$: Observable<Game[]> = this.gameStore.newToSkolGames$;
        newToSkolGames$.pipe(takeUntil(this.destroy$), filter(x => !!x)).subscribe(games => {
          const newToSkolGames = this.orderStaticGamesForSlider(games, slidersGames.newToSkol); // remove this line if games are not static
          this.launchCodes = [];
          newToSkolGames.map(game => this.launchCodes.push(game.launchcode));
        });
        break;

      case (GameTypesEnum.BecauseYouPlayedFromCurrentGame):
        if (!this.currentGame) {
          new Error('You need to provide a currentGame as an input to use this category');
        }
        if (this.launchCodes.length < 1) {
          this.gameName = this.currentGame.name;
          this.gameStore.generateBecauseYouPlayedGamesFromCurrentGame(this.currentGame);
          const becauseYouPlayedGamesFromCurrentGame$ = this.gameStore.becauseYouPlayedFromCurrentGame$;
          becauseYouPlayedGamesFromCurrentGame$.pipe(takeUntil(this.destroy$), filter(x => !!x && this.launchCodes.length < 1)).subscribe(games => {
            this.launchCodes = [];
            games.map(game => this.launchCodes.push(game.launchcode));
            /*
            * We need this here as this slider-type is in the game-details modal
            */
          });
          this.cdr.detectChanges();
        }
        break;

      case (GameTypesEnum.ProviderFromCurrentGame):
        if (!this.currentGame) {
          new Error('You need to provide a currentGame as an input to use this category');
        }
        if (this.launchCodes.length < 1) {
          this.gameName = this.currentGame.provider;
          this.gameStore.generateProviderGamesFromCurrentGame(this.currentGame);
          const providerGamesFromCurrentGames$ = this.gameStore.providerGamesFromCurrentGame$;
          providerGamesFromCurrentGames$.pipe(takeUntil(this.destroy$), filter(x => !!x && this.launchCodes.length < 1)).subscribe(games => {
            this.launchCodes = [];
            const provider: string = games[0].provider;
            this.type.config.browseAllLink = `/games?provider=${this.gameFilterCategoriesService.createProviderId(provider)}`;
            games.map(game => this.launchCodes.push(game.launchcode));
            /*
            * We need this here as this slider-type is in the game-details modal
            */
          });
          this.cdr.detectChanges();
        }
        break;

      case (GameTypesEnum.Favorites):
        this.favoritesStore.getFavorites();
        // @todo - change Set<Game> type in favorites to Game[]
        const favoriteGames$: Observable<Set<Game>> = this.favoritesStore.favorites$;
        favoriteGames$.pipe(takeUntil(this.destroy$), filter(x => !!x)).subscribe(games => {
          this.launchCodes = [];
          games.forEach(game => {
            this.launchCodes.push(game.launchcode);
          });
        });
        break;

      case (GameTypesEnum.Jackpots):
        /*
        * Currency is gotten from account details if logged in,
        * otherwise its changed based on jurisdiction/country code
        */
        const gameJackpotsData$ = this.currencyCode$.pipe(
          filter(x => !!x),
          switchMap((currencyCode: string) => {
            return this.gameStore.groupedByCategoryGames$.pipe(
              filter(x => !!x),
              switchMap(groupedGames => {
                let jackpotsData: JackpotsCurrencyWithGames = {
                  currency: currencyCode,
                  games: groupedGames.jackpots.popular
                }
                return of(jackpotsData);
              }));
          }));
        gameJackpotsData$.pipe(takeUntil(this.destroy$), filter(x => !!x)).subscribe(value => {
          this.jackpotsStore.fakeJackpots(value);
        });
        this.jackpotsStore.fakedJackpots$.pipe(takeUntil(this.destroy$), filter(x => !!x)).subscribe(gameJackpots => {
          this.launchCodes = [];
          this.imgCodes = [{}];
          this.amounts = [];
          gameJackpots = this.orderStaticGamesForSlider(gameJackpots, slidersGames.jackpots) // remove this line if games are not static
          gameJackpots.forEach(jackpot => {
            let formattedAmount: string = jackpot.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            this.launchCodes.push(jackpot.launchcode);
            this.amounts.push(formattedAmount);
          });
        });
        break;

      case (GameTypesEnum.TableGames):
        const groupedGames$: Observable<GameTypeGames> = this.gameStore.groupedByCategoryGames$;
        groupedGames$.pipe(takeUntil(this.destroy$), filter(x => !!x)).subscribe(games => {
          this.launchCodes = [];
          this.imgCodes = [{}];
          const tableGames = this.orderStaticGamesForSlider(games.classic.popular, slidersGames.tableGames);
          tableGames.forEach(game => {
            this.imgCodes.push({ launchCode: game.launchcode, imgCode: game.image });
            this.launchCodes.push(game.launchcode)
          });
        });
        break;

      case (GameTypesEnum.Slots):
        this.setImageAndLaunchCodes(GameTypesEnum.Slots, 'popular');
        break;

      case (GameTypesEnum.Fun):
        this.setImageAndLaunchCodes(GameTypesEnum.Fun, 'popular');
        break;

      case (GameTypesEnum.Popular):
        this.setImageAndLaunchCodes(GameTypesEnum.Popular, 'popular');
        break;

      case (GameTypesEnum.StickyWilds):
        this.setImageAndLaunchCodes(GameTypesEnum.Fun, 'popular'); // TODO-A Put Scratch & Fun games for sticky wilds until categoty is ready
        break;

      case (GameTypesEnum.Providers):
        this.setImageAndLaunchCodes(GameTypesEnum.Popular, 'popular'); // TODO-A Put All(Popular) games for until provider categoty is ready
        break;

      case (GameTypesEnum.LiveGames):
        const groupedLiveGames$: Observable<GameTypeGames> = this.gameStore.groupedByCategoryGames$;
        groupedLiveGames$.pipe(takeUntil(this.destroy$), filter(x => !!x)).subscribe(games => {
          this.launchCodes = [];
          this.imgCodes = [{}];
          const liveGames = this.orderStaticGamesForSlider(games.live.popular, slidersGames.liveGames);
          liveGames.forEach(game => {
            this.imgCodes.push({ launchCode: game.launchcode, imgCode: game.image });
            this.launchCodes.push(game.launchcode)
          });
        });
        break;

      case (GameTypesEnum.MustGoJackpots):
        const groupedMustGoJackpotsGames$: Observable<GameTypeGames> = this.gameStore.groupedByCategoryGames$;
        groupedMustGoJackpotsGames$.pipe(takeUntil(this.destroy$), filter(x => !!x)).subscribe(games => {
          this.launchCodes = [];
          this.imgCodes = [{}];
          const mustGoJackpotsGames: Game[] = _.shuffle(games.mustgojackpots.popular);
          mustGoJackpotsGames.forEach(game => {
            this.imgCodes.push({ launchCode: game.launchcode, imgCode: game.image });
            this.launchCodes.push(game.launchcode)
          });
        });
        break;

      case (GameTypesEnum.DropsAndWins):
        const groupedDropsAndWinsGames$: Observable<GameTypeGames> = this.gameStore.groupedByCategoryGames$;
        groupedDropsAndWinsGames$.pipe(takeUntil(this.destroy$), filter(x => !!x)).subscribe(games => {
          this.launchCodes = [];
          this.imgCodes = [{}];
          const dropsAndWinsGames: Game[] = _.shuffle(games.dropsandwins.popular);
          dropsAndWinsGames.forEach(game => {
            this.imgCodes.push({ launchCode: game.launchcode, imgCode: game.image });
            this.launchCodes.push(game.launchcode)
          });
        });
        break;

      case (GameTypesEnum.Megaways):
        const groupedMegawayGames$: Observable<GameTypeGames> = this.gameStore.groupedByCategoryGames$;
        groupedMegawayGames$.pipe(takeUntil(this.destroy$), filter(x => !!x)).subscribe(games => {
          this.launchCodes = [];
          this.imgCodes = [{}];
          const megawayGames = this.orderStaticGamesForSlider(games.megaways.popular, slidersGames.megaways);
          megawayGames.forEach(game => {
            this.imgCodes.push({ launchCode: game.launchcode, imgCode: game.image });
            this.launchCodes.push(game.launchcode)
          });
        });
        break;

      default:
        break;
    }
  }

  sliderTitle(): string {
    return this.typeConfig[this.visualType].title;
  }

  titleWithGame(): boolean {
    return this.visualType === GameTypesEnum.BecauseYouPlayed ||
      this.visualType === GameTypesEnum.BecauseYouPlayedFromCurrentGame ||
      this.visualType === GameTypesEnum.ProviderFromCurrentGame ||
      this.visualType === GameTypesEnum.Provider
  }

  visualTypeSlideScaling(): object {
    const screenWidth = this.coreService.getScreenWidthFromWindow();
    const conf = this.typeConfig[this.visualType].config;

    /*
    * Switches between desktop and mobile styling
    */
    let flexStyle: string;
    if (screenWidth > 800) {
      flexStyle = '0 1 ' + (conf.min_width / (conf.ideal_width + conf.spacer)) * 100 + '%';
    } else {
      flexStyle = '1 0 ' + (conf.min_width / (conf.ideal_width + conf.spacer)) * 100 + '%';
    }
    return {
      'min-width': 'calc(var(--spacer) * ' + conf.min_width / conf.spacer + ')',
      'max-width': 'calc(var(--spacer) * ' + conf.max_width / conf.spacer + ')',
      flex: flexStyle
    };
  }

  generateImageLink(launchcode: string): string {
    // TODO-A This one to be moved to service + link in environment later or stay here depending on the further logic
    if (this.visualType === GameTypesEnum.Featured) {
      return 'assets/featured-games-slider/' + launchcode + '.jpg';
    } else if (this.visualType === GameTypesEnum.LiveGames || this.visualType === GameTypesEnum.TableGames) {
      const imgCode: string = this.imgCodes.filter(imgObj => {
        return imgObj.launchCode === launchcode
      })[0].imgCode;
      return `${this.coreService.scontentUrl}/images/portrait/${imgCode}.jpg`
    } else {
      return `${this.coreService.scontentUrl}/images/portrait/${launchcode}.jpg`
    }
  }

  openGameDetails(launchCode: string): void {
    // first we need to dismiss all open modals
    if (!this.isInSearch) {
      if (this.coreService.isModalOpenedByAriaLabel('game-details-title')) {
        this.gameOpenedFromSlider.next(true);
      }
      this.activeModal.close();
    }

    const gameByLaunchCode$: Observable<Game[]> = this.gameStore.getGameDetails(launchCode);
    gameByLaunchCode$.pipe(takeUntil(this.destroy$), filter(x => !!x && x.length > 0))
      .subscribe(() => {
        if (!this.coreService.isModalOpenedByAriaLabel('search-modal-title')) {
          if (!window.history.state) {
            const modalState = {
              modal: true,
              desc: 'modal opened'
            };
            history.pushState(modalState, null);
          }
        }
        this.modalService.open(GamesDetailsComponent, {
          ariaLabelledBy: "game-details-title",
          windowClass: "game-details-modal",
        }).result.then(() => {
        }).catch(() => { });
      }).unsubscribe();
  }

  openGame(launchCode: string): void {
    if (this.type.config.playBtn) {
      this.launchGame(launchCode);
    } else {
      this.openGameDetails(launchCode);
    }
  }

  launchGame(launchCode: string): void {
    window.location.href = `/play?id=${launchCode}&mode=real`;
  }

  getGameName(launchCode: string): Observable<string> {
    const gameByLaunchCode$: Observable<Game> = this.gameStore.getGameByLaunchCode(launchCode);
    return gameByLaunchCode$.pipe(
      filter(x => !!x),
      map(game => game.name));
  }

  getJackpotAmount(amount: string): string {
    return amount === "NaN" ? '' : amount;
  }

  private setImageAndLaunchCodes(categoty: string, type: string): void {
    const categoryGame$: Observable<GameTypeGames> = this.gameStore.groupedByCategoryGames$;
    categoryGame$.pipe(takeUntil(this.destroy$), filter(x => !!x)).subscribe(games => {
      this.launchCodes = [];
      this.imgCodes = [{}];
      const categotyGames: Game[] = _.shuffle(games[categoty][type]); // TODO-A To make static once we know the list of games and add those in slider.config.ts
      categotyGames.forEach(game => {
        this.imgCodes.push({ launchCode: game.launchcode, imgCode: game.image });
        this.launchCodes.push(game.launchcode)
      });
    });
  }

  private orderStaticGamesForSlider(games: Game[], staticGames: string[]): Game[] {
    const sliderGames = _.remove(games, (singleGameConfig: any) => { // find and exclude the needed games from the category
      return staticGames.includes(singleGameConfig.launchcode);
    });
    sliderGames.sort((a, b) => { // order games to be exactly as the config array
      return staticGames.indexOf(a.launchcode) - staticGames.indexOf(b.launchcode);
    });
    return [...sliderGames, ...games];
  }

}
